/**
 * usePlanExecution - Plan Execution Logic Hook
 * 
 * Extracts plan execution functionality from WorkspacePage.tsx:
 * - Plan document selection for @plan command
 * - Plan execution with step-by-step operation application
 * - Editor readiness checking
 * 
 * NOTE: The in-memory plan state functions (setPlanStatus, updatePlanStep, updatePlanDocument)
 * are deprecated. Plan execution now uses the backend document system.
 * The handleExecutePlan function is preserved for backward compatibility but
 * will be refactored to use the backend execution API.
 */

import { useCallback } from 'react';
import type { PlanDocument } from '../types/plan-document';
import type { AeroDocument } from '../types/document-schema';
import { logger } from '../utils/logger';

interface OpenedDocumentForPlan {
  tabId: number;
  id?: string;
  documentNumber?: string;
  projectId?: string;
  title: string;
  type?: string;
  canEdit?: boolean;
  status?: string;
  currentRevision?: string;
  jsonDocument?: AeroDocument;
  version?: string;
}

interface ActiveEditDocumentForPlan {
  tabId: number;
}

interface PlanMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp?: string;
}

interface UsePlanExecutionDeps {
  // From AIContext
  activeConversationId: string | null;
  openedDocuments: OpenedDocumentForPlan[];
  addOpenedDocument: (doc: OpenedDocumentForPlan) => void;
  setActiveTabId: (id: number | null) => void;
  activeEditDocument: ActiveEditDocumentForPlan | null;
  setAiMode: (mode: 'ask' | 'agent' | 'plan') => void;
  addMessage: (conversationId: string, message: PlanMessage) => void;
  
  // Edit mode
  handleEnableEdit: (tabId: number, jsonDocument: AeroDocument, version: string) => void;
  
  // Input control
  setInput: (value: string) => void;
  closeAllAutocomplete: () => void;
  setShowPlanAutocomplete: (show: boolean) => void;
  setPlanQuery: (query: string) => void;
}

interface DocumentSelectResult {
  id?: string;
  documentNumber?: string;
  title?: string;
  version?: string;
  jsonDocument?: AeroDocument;
}

interface UsePlanExecutionReturn {
  handlePlanDocumentSelect: (result: DocumentSelectResult) => void;
  handleExecutePlan: (plan: PlanDocument) => Promise<void>;
  waitForEditorReady: (maxWaitMs?: number) => Promise<boolean>;
}

export function usePlanExecution(deps: UsePlanExecutionDeps): UsePlanExecutionReturn {
  const {
    activeConversationId,
    openedDocuments,
    addOpenedDocument,
    setActiveTabId,
    activeEditDocument: _activeEditDocument,
    setAiMode,
    addMessage,
    handleEnableEdit: _handleEnableEdit,
    setInput,
    closeAllAutocomplete: _closeAllAutocomplete,
    setShowPlanAutocomplete,
    setPlanQuery,
  } = deps;

  /**
   * Wait for document editor to be ready
   */
  const waitForEditorReady = useCallback(async (maxWaitMs: number = 5000): Promise<boolean> => {
    const startTime = Date.now();
    const pollInterval = 100;
    
    while (Date.now() - startTime < maxWaitMs) {
      if (typeof window.__queueDocumentOperation === 'function') {
        logger.debug('[usePlanExecution] Editor ready after', Date.now() - startTime, 'ms');
        return true;
      }
      await new Promise(resolve => setTimeout(resolve, pollInterval));
    }
    
    logger.error('[usePlanExecution] Editor not ready after', maxWaitMs, 'ms');
    return false;
  }, []);

  /**
   * Handle document selection for @plan command
   */
  const handlePlanDocumentSelect = useCallback((result: DocumentSelectResult) => {
    logger.debug('[usePlanExecution] Starting Plan mode for document:', result.documentNumber);
    
    // Check if document is already open (by id OR documentNumber)
    const existingDoc = openedDocuments.find(d => 
      d.id === result.id || 
      (result.documentNumber && d.documentNumber === result.documentNumber)
    );
    
    if (existingDoc) {
      // Document is already open - just switch to it
      logger.debug('[usePlanExecution] Document already open, switching to tab:', existingDoc.tabId);
      setActiveTabId(existingDoc.tabId);
    } else {
      // Open the document
      logger.debug('[usePlanExecution] Opening document for plan mode');
      addOpenedDocument({
        id: result.id,
        documentNumber: result.documentNumber,
        projectId: result.projectId,
        title: result.title,
        type: result.type,
        canEdit: result.canEdit || false,
        status: result.status,
        currentRevision: result.currentRevision,
      });
      
      // Set as active tab after a brief delay
      setTimeout(() => {
        const doc = openedDocuments.find(d => d.id === result.id);
        if (doc) {
          setActiveTabId(doc.tabId);
        } else {
          const maxTabId = openedDocuments.length > 0 ? Math.max(...openedDocuments.map(d => d.tabId)) : 0;
          setActiveTabId(maxTabId + 1);
        }
      }, 100);
    }
    
    // Switch to Plan mode
    setAiMode('plan');
    
    // Clear input and autocomplete
    setInput('');
    setShowPlanAutocomplete(false);
    setPlanQuery('');
    
    // Show helpful guidance
    if (activeConversationId) {
      addMessage(activeConversationId, {
        role: 'assistant',
        content: `📋 **Plan Mode Activated** for **${result.title}** (${result.documentNumber})\n\nI'm ready to help you create an editing plan for this document. Describe what changes you'd like to make, and I'll create a step-by-step plan that you can review before executing.\n\nFor example:\n- "Add an Executive Summary section before Section 1"\n- "Update the revision history table"\n- "Rewrite Section 2.1 to clarify the scope"`,
        timestamp: new Date(),
      });
    }
  }, [activeConversationId, openedDocuments, addOpenedDocument, setActiveTabId, setAiMode, addMessage, setInput, setShowPlanAutocomplete, setPlanQuery]);

  /**
   * Execute a plan - triggers backend execution API
   * 
   * NOTE: Plan execution now happens via the backend. This function
   * should trigger the backend execution API rather than applying
   * operations locally.
   */
  const handleExecutePlan = useCallback(async (plan: PlanDocument) => {
    logger.debug('[usePlanExecution] Executing plan:', plan.id, plan.title);
    
    // Find the target document tab
    const targetDoc = openedDocuments.find(
      d => d.documentNumber === plan.targetDocument.documentNumber && 
           d.projectId === plan.targetDocument.projectId
    );
    
    if (!targetDoc) {
      logger.error('[usePlanExecution] Target document not open:', plan.targetDocument.documentNumber);
      throw new Error(`Target document ${plan.targetDocument.documentNumber} is not open. Please open it first.`);
    }
    
    // Switch to the target document tab
    setActiveTabId(targetDoc.tabId);
    
    // TODO: Implement backend execution API call
    // For now, log that execution is not yet implemented
    logger.warn('[usePlanExecution] Backend plan execution not yet implemented');
    throw new Error('Plan execution via backend is not yet implemented. Please use the AI to execute plans interactively.');
  }, [openedDocuments, setActiveTabId]);

  return {
    handlePlanDocumentSelect,
    handleExecutePlan,
    waitForEditorReady,
  };
}
